import { logError } from "./log";

/**
 * Normalizes a given string (URL or hostname) to a consistent hostname format.
 * It extracts the hostname if a full URL is provided and removes the leading 'www.' prefix.
 *
 * @param hostnameOrUrl - The input string, which can be a full URL (e.g., "https://www.example.com/path")
 *                        or just a hostname (e.g., "www.example.com", "example.com").
 * @returns The normalized hostname (e.g., "example.com") or an empty string if the input is invalid or cannot be processed.
 */
export const normalizeUrl = (
  hostnameOrUrl: string | undefined | null
): string => {
  if (!hostnameOrUrl) {
    return "";
  }

  let hostname: string;

  try {
    // Trim the input to remove leading/trailing spaces
    hostnameOrUrl = hostnameOrUrl.trim();

    // Check if it looks like a full URL
    if (hostnameOrUrl.startsWith("//")) {
      // Handle protocol-relative URLs
      hostnameOrUrl = "https:" + hostnameOrUrl;
    }

    if (hostnameOrUrl.includes("://")) {
      const urlObj = new URL(hostnameOrUrl);
      hostname = urlObj.hostname;
    } else if (hostnameOrUrl.includes(".") || hostnameOrUrl === "localhost") {
      // Assume it's a hostname if it contains a dot or is 'localhost'
      hostname = hostnameOrUrl;
    } else {
      // Does not look like a URL or a valid hostname
      logError(
        "Input does not appear to be a valid URL or hostname:",
        hostnameOrUrl
      );
      return "";
    }

    // Remove leading 'www.' and convert to lowercase
    return hostname.replace(/^www\./i, "").toLowerCase();
  } catch (error) {
    logError("Error normalizing URL/hostname:", { hostnameOrUrl, error });
    return "";
  }
};
